import { useCallback, useState, useRef, useEffect } from "react";
import type {
  Warehouse,
  Zone,
  Slot,
  ViewState,
  Rect,
  Position,
  InventoryItem,
} from "./warehouse-types";

// Zone colors (teal/green tones)
const ZONE_COLORS = [
  "rgba(45, 212, 191, 0.3)", // teal
  "rgba(52, 211, 153, 0.3)", // emerald
  "rgba(74, 222, 128, 0.3)", // green
  "rgba(163, 230, 53, 0.3)", // lime
  "rgba(250, 204, 21, 0.3)", // yellow
];

// Slot colors (blue/indigo tones)
const SLOT_COLORS = [
  "rgba(96, 165, 250, 0.35)", // blue
  "rgba(129, 140, 248, 0.35)", // indigo
  "rgba(168, 85, 247, 0.35)", // purple
  "rgba(192, 132, 252, 0.35)", // violet
];

// Empty layout used when a warehouse has no saved layoutConfig yet
const createEmptyWarehouse = (): Warehouse => ({
  id: "",
  name: "",
  floorPlanUrl: null,
  zones: [],
});

// Default placement of a freshly uploaded floor plan (canvas coordinates)
export const DEFAULT_FLOOR_PLAN_RECT: Rect = {
  x: 0,
  y: 0,
  width: 1200,
  height: 800,
};

export type ResizeHandle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

export function useWarehouse(initial?: Warehouse) {
  const [warehouse, setWarehouse] = useState<Warehouse>(
    () => initial ?? createEmptyWarehouse()
  );

  const [viewState, setViewState] = useState<ViewState>({
    mode: "warehouse",
    zoneId: null,
    scale: 1,
    offset: { x: 0, y: 0 },
  });

  // Keep a ref to the latest viewState so the non-passive wheel listener
  // (attached once) always reads fresh scale/offset without re-binding.
  const viewStateRef = useRef(viewState);
  viewStateRef.current = viewState;

  // Selection state - unified for zones and slots
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  // Active tool: "select" drags to pan/move, "draw" drags to create zones,
  // "floorplan" drags/resizes the floor plan image
  const [tool, setTool] = useState<"select" | "draw" | "floorplan">("select");

  // Drawing state for zones only (warehouse mode)
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<Position | null>(null);
  const [currentRect, setCurrentRect] = useState<Rect | null>(null);

  // Resizing state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle | null>(null);
  const [resizeStart, setResizeStart] = useState<{
    pos: Position;
    rect: Rect;
  } | null>(null);

  // Dragging/moving state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ pos: Position; rect: Rect } | null>(
    null
  );

  // Placing a new slot via pointer drag (consistent with zone/slot dragging)
  const [placingSlot, setPlacingSlot] = useState(false);
  const [dragSlotPreview, setDragSlotPreview] = useState<Position | null>(null);
  const [placingCursor, setPlacingCursor] = useState<Position | null>(null);

  const canvasContainerRef = useRef<HTMLDivElement>(null);

  // Get the current zone when viewing a zone
  const currentZone = viewState.zoneId
    ? warehouse.zones.find((z) => z.id === viewState.zoneId) || null
    : null;

  // Get the selected slot
  const selectedSlot = selectedSlotId
    ? warehouse.zones
        .flatMap((z) => z.slots)
        .find((s) => s.id === selectedSlotId) || null
    : null;

  // Get the selected zone (for editing in warehouse mode)
  const selectedZone = selectedZoneId
    ? warehouse.zones.find((z) => z.id === selectedZoneId) || null
    : null;

  // Clear selection when changing views
  const clearSelection = useCallback(() => {
    setSelectedZoneId(null);
    setSelectedSlotId(null);
  }, []);

  // Center (and fit) the floor plan in the viewport when a warehouse is first
  // loaded, so opening the page frames the background image in the middle
  // instead of anchoring it to the top-left corner.
  const didInitialCenterRef = useRef(false);
  useEffect(() => {
    if (didInitialCenterRef.current) return;
    const container = canvasContainerRef.current;
    if (!container) return;

    const center = () => {
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      if (!containerWidth || !containerHeight) return false;

      const rect = warehouse.floorPlanRect ?? DEFAULT_FLOOR_PLAN_RECT;
      const padding = 40;
      const scaleX = (containerWidth - padding * 2) / rect.width;
      const scaleY = (containerHeight - padding * 2) / rect.height;
      const newScale = Math.max(0.25, Math.min(scaleX, scaleY, 1));

      const centerX = rect.x + rect.width / 2;
      const centerY = rect.y + rect.height / 2;
      setViewState({
        mode: "warehouse",
        zoneId: null,
        scale: newScale,
        offset: {
          x: containerWidth / 2 - centerX * newScale,
          y: containerHeight / 2 - centerY * newScale,
        },
      });
      return true;
    };

    if (center()) {
      didInitialCenterRef.current = true;
    } else {
      const raf = requestAnimationFrame(() => {
        if (center()) didInitialCenterRef.current = true;
      });
      return () => cancelAnimationFrame(raf);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Upload floor plan (give it a default rect if it doesn't have one yet)
  const setFloorPlan = useCallback((url: string) => {
    setWarehouse((prev) => ({
      ...prev,
      floorPlanUrl: url,
      floorPlanRect: prev.floorPlanRect ?? DEFAULT_FLOOR_PLAN_RECT,
    }));
  }, []);

  // Clear floor plan
  const clearFloorPlan = useCallback(() => {
    setWarehouse((prev) => ({
      ...prev,
      floorPlanUrl: null,
      floorPlanRect: undefined,
    }));
    setTool((prev) => (prev === "floorplan" ? "select" : prev));
  }, []);

  // Move the floor plan image
  const moveFloorPlan = useCallback((newX: number, newY: number) => {
    setWarehouse((prev) => {
      const base = prev.floorPlanRect ?? DEFAULT_FLOOR_PLAN_RECT;
      return { ...prev, floorPlanRect: { ...base, x: newX, y: newY } };
    });
  }, []);

  // Resize the floor plan image
  const resizeFloorPlan = useCallback((newRect: Rect) => {
    setWarehouse((prev) => ({ ...prev, floorPlanRect: newRect }));
  }, []);

  // Add a zone
  const addZone = useCallback(
    (rect: Rect) => {
      const zoneCount = warehouse.zones.length;
      const zoneName = String.fromCharCode(65 + zoneCount); // A, B, C...
      const newZone: Zone = {
        id: `zone-${Date.now()}`,
        name: zoneName,
        rect,
        color: ZONE_COLORS[zoneCount % ZONE_COLORS.length],
        slots: [],
      };
      setWarehouse((prev) => ({
        ...prev,
        zones: [...prev.zones, newZone],
      }));
      // Select the newly created zone
      setSelectedZoneId(newZone.id);
    },
    [warehouse.zones.length]
  );

  // Add a slot to a zone (via drag-drop)
  const addSlotToZone = useCallback(
    (zoneId: string, dropPosition: Position) => {
      const zone = warehouse.zones.find((z) => z.id === zoneId);
      if (!zone) return;

      const slotCount = zone.slots.length + 1;
      const slotWidth = 120;
      const slotHeight = 80;

      // Ensure slot stays within zone bounds
      const x = Math.max(
        zone.rect.x + 10,
        Math.min(
          dropPosition.x - slotWidth / 2,
          zone.rect.x + zone.rect.width - slotWidth - 10
        )
      );
      const y = Math.max(
        zone.rect.y + 30,
        Math.min(
          dropPosition.y - slotHeight / 2,
          zone.rect.y + zone.rect.height - slotHeight - 10
        )
      );

      const newSlot: Slot = {
        id: `slot-${Date.now()}`,
        name: `${zone.name}-${slotCount}`,
        rect: { x, y, width: slotWidth, height: slotHeight },
        color: SLOT_COLORS[(slotCount - 1) % SLOT_COLORS.length],
        items: [],
      };

      setWarehouse((prev) => ({
        ...prev,
        zones: prev.zones.map((z) => {
          if (z.id !== zoneId) return z;
          return { ...z, slots: [...z.slots, newSlot] };
        }),
      }));

      // Select the newly created slot
      setSelectedSlotId(newSlot.id);
    },
    [warehouse.zones]
  );

  // Delete selected zone
  const deleteZone = useCallback((zoneId: string) => {
    setWarehouse((prev) => ({
      ...prev,
      zones: prev.zones.filter((z) => z.id !== zoneId),
    }));
    setSelectedZoneId(null);
  }, []);

  // Delete selected slot
  const deleteSlot = useCallback((slotId: string) => {
    setWarehouse((prev) => ({
      ...prev,
      zones: prev.zones.map((zone) => ({
        ...zone,
        slots: zone.slots.filter((s) => s.id !== slotId),
      })),
    }));
    setSelectedSlotId(null);
  }, []);

  // Move zone
  const moveZone = useCallback((zoneId: string, newX: number, newY: number) => {
    setWarehouse((prev) => ({
      ...prev,
      zones: prev.zones.map((zone) => {
        if (zone.id !== zoneId) return zone;
        const deltaX = newX - zone.rect.x;
        const deltaY = newY - zone.rect.y;
        return {
          ...zone,
          rect: { ...zone.rect, x: newX, y: newY },
          // Also move all slots within this zone
          slots: zone.slots.map((slot) => ({
            ...slot,
            rect: {
              ...slot.rect,
              x: slot.rect.x + deltaX,
              y: slot.rect.y + deltaY,
            },
          })),
        };
      }),
    }));
  }, []);

  // Move slot - constrained within its parent zone so it can't move to another zone
  const moveSlot = useCallback((slotId: string, newX: number, newY: number) => {
    setWarehouse((prev) => ({
      ...prev,
      zones: prev.zones.map((zone) => {
        const slot = zone.slots.find((s) => s.id === slotId);
        if (!slot) return zone;

        const clampedX = Math.max(
          zone.rect.x,
          Math.min(newX, zone.rect.x + zone.rect.width - slot.rect.width)
        );
        const clampedY = Math.max(
          zone.rect.y,
          Math.min(newY, zone.rect.y + zone.rect.height - slot.rect.height)
        );

        return {
          ...zone,
          slots: zone.slots.map((s) =>
            s.id === slotId
              ? { ...s, rect: { ...s.rect, x: clampedX, y: clampedY } }
              : s
          ),
        };
      }),
    }));
  }, []);

  // Rename zone
  const renameZone = useCallback((zoneId: string, name: string) => {
    setWarehouse((prev) => ({
      ...prev,
      zones: prev.zones.map((zone) =>
        zone.id === zoneId ? { ...zone, name } : zone
      ),
    }));
  }, []);

  // Rename slot
  const renameSlot = useCallback((slotId: string, name: string) => {
    setWarehouse((prev) => ({
      ...prev,
      zones: prev.zones.map((zone) => ({
        ...zone,
        slots: zone.slots.map((slot) =>
          slot.id === slotId ? { ...slot, name } : slot
        ),
      })),
    }));
  }, []);

  // Resize zone
  const resizeZone = useCallback((zoneId: string, newRect: Rect) => {
    setWarehouse((prev) => ({
      ...prev,
      zones: prev.zones.map((zone) =>
        zone.id === zoneId ? { ...zone, rect: newRect } : zone
      ),
    }));
  }, []);

  // Resize slot - constrained within its parent zone so its edges can't pass the zone bounds
  const resizeSlot = useCallback((slotId: string, newRect: Rect) => {
    setWarehouse((prev) => ({
      ...prev,
      zones: prev.zones.map((zone) => {
        if (!zone.slots.some((s) => s.id === slotId)) return zone;

        const left = Math.max(newRect.x, zone.rect.x);
        const top = Math.max(newRect.y, zone.rect.y);
        const right = Math.min(
          newRect.x + newRect.width,
          zone.rect.x + zone.rect.width
        );
        const bottom = Math.min(
          newRect.y + newRect.height,
          zone.rect.y + zone.rect.height
        );

        const clampedRect = {
          x: left,
          y: top,
          width: right - left,
          height: bottom - top,
        };

        return {
          ...zone,
          slots: zone.slots.map((slot) =>
            slot.id === slotId ? { ...slot, rect: clampedRect } : slot
          ),
        };
      }),
    }));
  }, []);

  // Zoom to zone
  const zoomToZone = useCallback(
    (zoneId: string) => {
      const zone = warehouse.zones.find((z) => z.id === zoneId);
      if (!zone || !canvasContainerRef.current) return;

      const container = canvasContainerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Calculate scale to fit zone with padding
      const padding = 100;
      const scaleX = (containerWidth - padding * 2) / zone.rect.width;
      const scaleY = (containerHeight - padding * 2) / zone.rect.height;
      const newScale = Math.min(scaleX, scaleY, 3);

      // Calculate offset to center the zone
      const centerX = zone.rect.x + zone.rect.width / 2;
      const centerY = zone.rect.y + zone.rect.height / 2;
      const newOffset = {
        x: containerWidth / 2 - centerX * newScale,
        y: containerHeight / 2 - centerY * newScale,
      };

      setViewState({
        mode: "zone",
        zoneId,
        scale: newScale,
        offset: newOffset,
      });
      clearSelection();
    },
    [warehouse.zones, clearSelection]
  );

  // Zoom out to warehouse view
  const zoomOutToWarehouse = useCallback(() => {
    setViewState({
      mode: "warehouse",
      zoneId: null,
      scale: 1,
      offset: { x: 0, y: 0 },
    });
    clearSelection();
  }, [clearSelection]);

  // Zoom controls
  const zoomIn = useCallback(() => {
    const container = canvasContainerRef.current;
    if (!container) return;
    const centerX = container.clientWidth / 2;
    const centerY = container.clientHeight / 2;
    const scaleFactor = 1.25;
    const newScale = Math.min(5, viewState.scale * scaleFactor);
    const newOffset = {
      x: centerX - ((centerX - viewState.offset.x) / viewState.scale) * newScale,
      y: centerY - ((centerY - viewState.offset.y) / viewState.scale) * newScale,
    };
    setViewState((prev) => ({ ...prev, scale: newScale, offset: newOffset }));
  }, [viewState]);

  const zoomOut = useCallback(() => {
    const container = canvasContainerRef.current;
    if (!container) return;
    const centerX = container.clientWidth / 2;
    const centerY = container.clientHeight / 2;
    const scaleFactor = 0.8;
    const newScale = Math.max(0.25, viewState.scale * scaleFactor);
    const newOffset = {
      x: centerX - ((centerX - viewState.offset.x) / viewState.scale) * newScale,
      y: centerY - ((centerY - viewState.offset.y) / viewState.scale) * newScale,
    };
    setViewState((prev) => ({ ...prev, scale: newScale, offset: newOffset }));
  }, [viewState]);

  // Update slot items
  const updateSlotItem = useCallback(
    (slotId: string, itemId: string, updates: Partial<InventoryItem>) => {
      setWarehouse((prev) => ({
        ...prev,
        zones: prev.zones.map((zone) => ({
          ...zone,
          slots: zone.slots.map((slot) => {
            if (slot.id !== slotId) return slot;
            return {
              ...slot,
              items: slot.items.map((item) =>
                item.id === itemId ? { ...item, ...updates } : item
              ),
            };
          }),
        })),
      }));
    },
    []
  );

  // Add item to slot
  const addSlotItem = useCallback((slotId: string, item: InventoryItem) => {
    setWarehouse((prev) => ({
      ...prev,
      zones: prev.zones.map((zone) => ({
        ...zone,
        slots: zone.slots.map((slot) => {
          if (slot.id !== slotId) return slot;
          return { ...slot, items: [...slot.items, item] };
        }),
      })),
    }));
  }, []);

  // Delete item from slot
  const deleteSlotItem = useCallback((slotId: string, itemId: string) => {
    setWarehouse((prev) => ({
      ...prev,
      zones: prev.zones.map((zone) => ({
        ...zone,
        slots: zone.slots.map((slot) => {
          if (slot.id !== slotId) return slot;
          return { ...slot, items: slot.items.filter((i) => i.id !== itemId) };
        }),
      })),
    }));
  }, []);

  // Handle keyboard events (Delete key, zoom shortcuts)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't act on shortcuts while typing in an input
      const isTyping =
        (e.target as HTMLElement).tagName === "INPUT" ||
        (e.target as HTMLElement).tagName === "TEXTAREA";

      if (e.key === "Delete" || e.key === "Backspace") {
        if (isTyping) return;

        if (selectedSlotId) {
          deleteSlot(selectedSlotId);
        } else if (selectedZoneId && viewState.mode === "warehouse") {
          deleteZone(selectedZoneId);
        }
      }

      // Escape to deselect
      if (e.key === "Escape") {
        clearSelection();
      }

      // Keyboard zoom for mouse+keyboard users: +/= zoom in, -/_ zoom out
      if (!isTyping) {
        if (e.key === "+" || e.key === "=") {
          e.preventDefault();
          zoomIn();
        } else if (e.key === "-" || e.key === "_") {
          e.preventDefault();
          zoomOut();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedSlotId,
    selectedZoneId,
    viewState.mode,
    deleteSlot,
    deleteZone,
    clearSelection,
    zoomIn,
    zoomOut,
  ]);

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback(
    (screenX: number, screenY: number): Position => {
      const container = canvasContainerRef.current;
      if (!container) return { x: 0, y: 0 };

      const containerRect = container.getBoundingClientRect();
      const x = (screenX - containerRect.left - viewState.offset.x) / viewState.scale;
      const y = (screenY - containerRect.top - viewState.offset.y) / viewState.scale;
      return { x, y };
    },
    [viewState]
  );

  // Mouse handlers for drawing zones (warehouse mode only)
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.button !== 0) return; // Only left click

      // Only draw zones when the draw tool is active
      if (tool !== "draw") return;

      // Only allow drawing in warehouse mode
      if (viewState.mode !== "warehouse") return;

      // Don't start drawing if we're already resizing or dragging
      if (isResizing || isDragging) return;

      // Don't start drawing if clicking on a zone (let zone handle it)
      // Check if the click target is inside a zone element
      const target = e.target as HTMLElement;
      const isClickOnZone = target.closest(
        '[data-canvas-layer] > div:not([class*="pointer-events-none"])'
      );
      if (isClickOnZone) return;

      const container = canvasContainerRef.current;
      if (!container) return;

      const pos = screenToCanvas(e.clientX, e.clientY);

      setIsDrawing(true);
      setDrawStart(pos);
      setCurrentRect({ x: pos.x, y: pos.y, width: 0, height: 0 });

      // Deselect when starting to draw
      clearSelection();
    },
    [tool, viewState.mode, screenToCanvas, clearSelection, isResizing, isDragging]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Handle dragging/moving
      if (isDragging && dragStart) {
        const pos = screenToCanvas(e.clientX, e.clientY);
        const deltaX = pos.x - dragStart.pos.x;
        const deltaY = pos.y - dragStart.pos.y;
        const newX = dragStart.rect.x + deltaX;
        const newY = dragStart.rect.y + deltaY;

        if (tool === "floorplan") {
          moveFloorPlan(newX, newY);
        } else if (selectedZoneId) {
          moveZone(selectedZoneId, newX, newY);
        } else if (selectedSlotId) {
          moveSlot(selectedSlotId, newX, newY);
        }
        return;
      }

      // Handle resizing
      if (isResizing && resizeStart && resizeHandle) {
        const pos = screenToCanvas(e.clientX, e.clientY);
        const { rect } = resizeStart;
        let newRect = { ...rect };

        const minSize = 50;

        switch (resizeHandle) {
          case "nw":
            newRect = {
              x: Math.min(pos.x, rect.x + rect.width - minSize),
              y: Math.min(pos.y, rect.y + rect.height - minSize),
              width:
                rect.x + rect.width - Math.min(pos.x, rect.x + rect.width - minSize),
              height:
                rect.y + rect.height - Math.min(pos.y, rect.y + rect.height - minSize),
            };
            break;
          case "n":
            newRect = {
              ...rect,
              y: Math.min(pos.y, rect.y + rect.height - minSize),
              height:
                rect.y + rect.height - Math.min(pos.y, rect.y + rect.height - minSize),
            };
            break;
          case "ne":
            newRect = {
              ...rect,
              y: Math.min(pos.y, rect.y + rect.height - minSize),
              width: Math.max(minSize, pos.x - rect.x),
              height:
                rect.y + rect.height - Math.min(pos.y, rect.y + rect.height - minSize),
            };
            break;
          case "e":
            newRect = { ...rect, width: Math.max(minSize, pos.x - rect.x) };
            break;
          case "se":
            newRect = {
              ...rect,
              width: Math.max(minSize, pos.x - rect.x),
              height: Math.max(minSize, pos.y - rect.y),
            };
            break;
          case "s":
            newRect = { ...rect, height: Math.max(minSize, pos.y - rect.y) };
            break;
          case "sw":
            newRect = {
              x: Math.min(pos.x, rect.x + rect.width - minSize),
              y: rect.y,
              width:
                rect.x + rect.width - Math.min(pos.x, rect.x + rect.width - minSize),
              height: Math.max(minSize, pos.y - rect.y),
            };
            break;
          case "w":
            newRect = {
              ...rect,
              x: Math.min(pos.x, rect.x + rect.width - minSize),
              width:
                rect.x + rect.width - Math.min(pos.x, rect.x + rect.width - minSize),
            };
            break;
        }

        if (tool === "floorplan") {
          resizeFloorPlan(newRect);
        } else if (selectedZoneId) {
          resizeZone(selectedZoneId, newRect);
        } else if (selectedSlotId) {
          resizeSlot(selectedSlotId, newRect);
        }
        return;
      }

      // Handle drawing
      if (!isDrawing || !drawStart) return;

      const pos = screenToCanvas(e.clientX, e.clientY);

      const rect: Rect = {
        x: Math.min(drawStart.x, pos.x),
        y: Math.min(drawStart.y, pos.y),
        width: Math.abs(pos.x - drawStart.x),
        height: Math.abs(pos.y - drawStart.y),
      };
      setCurrentRect(rect);
    },
    [
      isDrawing,
      drawStart,
      isDragging,
      dragStart,
      isResizing,
      resizeStart,
      resizeHandle,
      screenToCanvas,
      selectedZoneId,
      selectedSlotId,
      resizeZone,
      resizeSlot,
      moveZone,
      moveSlot,
      tool,
      moveFloorPlan,
      resizeFloorPlan,
    ]
  );

  const handleMouseUp = useCallback(() => {
    // Handle drag end
    if (isDragging) {
      setIsDragging(false);
      setDragStart(null);
      return;
    }

    // Handle resize end
    if (isResizing) {
      setIsResizing(false);
      setResizeHandle(null);
      setResizeStart(null);
      return;
    }

    // Handle drawing end
    if (!isDrawing || !currentRect) {
      setIsDrawing(false);
      return;
    }

    // Only create if the rect is big enough and in warehouse mode
    if (
      currentRect.width > 30 &&
      currentRect.height > 30 &&
      viewState.mode === "warehouse"
    ) {
      addZone(currentRect);
    }

    setIsDrawing(false);
    setDrawStart(null);
    setCurrentRect(null);
  }, [isDrawing, currentRect, viewState.mode, addZone, isResizing, isDragging]);

  // Start resizing
  const startResize = useCallback((handle: ResizeHandle, rect: Rect) => {
    setIsResizing(true);
    setResizeHandle(handle);
    setResizeStart({ pos: { x: 0, y: 0 }, rect });
  }, []);

  // Start dragging/moving
  const startDrag = useCallback(
    (clientX: number, clientY: number, rect: Rect) => {
      const pos = screenToCanvas(clientX, clientY);
      setIsDragging(true);
      setDragStart({ pos, rect });
    },
    [screenToCanvas]
  );

  // Start placing a new slot; tracking happens via window listeners below
  const startPlacingSlot = useCallback((clientX: number, clientY: number) => {
    setPlacingSlot(true);
    setPlacingCursor({ x: clientX, y: clientY });
  }, []);

  const isInsideCanvas = useCallback((clientX: number, clientY: number) => {
    const container = canvasContainerRef.current;
    if (!container) return false;
    const r = container.getBoundingClientRect();
    return (
      clientX >= r.left &&
      clientX <= r.right &&
      clientY >= r.top &&
      clientY <= r.bottom
    );
  }, []);

  useEffect(() => {
    if (!placingSlot) return;

    const handleMove = (e: PointerEvent) => {
      setPlacingCursor({ x: e.clientX, y: e.clientY });
      if (viewState.mode !== "zone" || !isInsideCanvas(e.clientX, e.clientY)) {
        setDragSlotPreview(null);
        return;
      }
      setDragSlotPreview(screenToCanvas(e.clientX, e.clientY));
    };

    const handleUp = (e: PointerEvent) => {
      if (
        e.type !== "pointercancel" &&
        viewState.mode === "zone" &&
        viewState.zoneId &&
        isInsideCanvas(e.clientX, e.clientY)
      ) {
        addSlotToZone(viewState.zoneId, screenToCanvas(e.clientX, e.clientY));
      }
      setPlacingSlot(false);
      setDragSlotPreview(null);
      setPlacingCursor(null);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [
    placingSlot,
    viewState.mode,
    viewState.zoneId,
    screenToCanvas,
    isInsideCanvas,
    addSlotToZone,
  ]);

  // Zoom with mouse wheel, trackpad pinch (ctrlKey), or trackpad two-finger pan.
  //
  // Attached as a NATIVE, non-passive listener (not React's onWheel): React
  // registers onWheel passively, so e.preventDefault() there is ignored and the
  // browser applies its own page zoom on pinch/ctrl+wheel — scaling the whole
  // outer frame instead of only the canvas. A non-passive listener lets
  // preventDefault() work, so only the canvas layer transforms.
  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();

      const containerRect = container.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      const mouseY = e.clientY - containerRect.top;
      const vs = viewStateRef.current;

      // Distinguish a real mouse wheel from a trackpad two-finger scroll:
      // mouse wheels report line/page deltas, or large integer vertical-only
      // steps, while trackpads report small/fractional pixel deltas often with a
      // horizontal component.
      const isMouseWheel =
        e.deltaMode !== 0 ||
        (e.deltaX === 0 && Number.isInteger(e.deltaY) && Math.abs(e.deltaY) >= 40);

      // Pinch zoom (ctrlKey) or a mouse wheel → zoom toward the cursor so PC
      // users with only a mouse can resize the view like a trackpad pinch does.
      if (e.ctrlKey || isMouseWheel) {
        const zoomingIn = e.deltaY < 0;
        // Bigger step per mouse-wheel notch; finer step for the many events a
        // trackpad pinch emits.
        const scaleFactor = e.ctrlKey
          ? zoomingIn
            ? 1.05
            : 0.95
          : zoomingIn
            ? 1.2
            : 0.8;
        const newScale = Math.max(0.25, Math.min(5, vs.scale * scaleFactor));

        const newOffset = {
          x: mouseX - ((mouseX - vs.offset.x) / vs.scale) * newScale,
          y: mouseY - ((mouseY - vs.offset.y) / vs.scale) * newScale,
        };

        setViewState((prev) => ({
          ...prev,
          scale: newScale,
          offset: newOffset,
        }));
      } else {
        // Two-finger pan (trackpad)
        const multiplier = e.deltaMode === 1 ? 16 : 1;

        setViewState((prev) => ({
          ...prev,
          offset: {
            x: prev.offset.x - e.deltaX * multiplier,
            y: prev.offset.y - e.deltaY * multiplier,
          },
        }));
      }
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, []);

  // Panning state
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<Position | null>(null);

  const handlePanStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Middle mouse button OR Alt+Left click for panning
      if (e.button === 1 || (e.button === 0 && e.altKey)) {
        e.preventDefault();
        setIsPanning(true);
        setPanStart({
          x: e.clientX - viewState.offset.x,
          y: e.clientY - viewState.offset.y,
        });
      }
    },
    [viewState.offset]
  );

  const handlePanMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isPanning || !panStart) return;
      setViewState((prev) => ({
        ...prev,
        offset: {
          x: e.clientX - panStart.x,
          y: e.clientY - panStart.y,
        },
      }));
    },
    [isPanning, panStart]
  );

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
    setPanStart(null);
  }, []);

  // Start panning from a plain pointer position (used by the select tool)
  const startPan = useCallback(
    (clientX: number, clientY: number) => {
      setIsPanning(true);
      setPanStart({
        x: clientX - viewState.offset.x,
        y: clientY - viewState.offset.y,
      });
    },
    [viewState.offset]
  );

  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;

    const touches = new Map<number, Position>();
    let gesture: {
      dist: number;
      mid: Position;
      scale: number;
      offset: Position;
    } | null = null;

    const measure = () => {
      const [a, b] = Array.from(touches.values());
      const r = container.getBoundingClientRect();
      return {
        dist: Math.hypot(b.x - a.x, b.y - a.y) || 1,
        mid: { x: (a.x + b.x) / 2 - r.left, y: (a.y + b.y) / 2 - r.top },
      };
    };

    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (touches.size === 2 && !gesture) {
        setIsDrawing(false);
        setDrawStart(null);
        setCurrentRect(null);
        setIsDragging(false);
        setDragStart(null);
        setIsResizing(false);
        setResizeHandle(null);
        setResizeStart(null);
        setIsPanning(false);
        setPanStart(null);
        const vs = viewStateRef.current;
        gesture = { ...measure(), scale: vs.scale, offset: vs.offset };
      }
      if (gesture) e.stopPropagation();
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "touch" || !touches.has(e.pointerId)) return;
      touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (!gesture) return;
      e.stopPropagation();
      if (touches.size < 2) return;

      const { dist, mid } = measure();
      const newScale = Math.max(
        0.25,
        Math.min(5, gesture.scale * (dist / gesture.dist))
      );
      const cx = (gesture.mid.x - gesture.offset.x) / gesture.scale;
      const cy = (gesture.mid.y - gesture.offset.y) / gesture.scale;
      setViewState((prev) => ({
        ...prev,
        scale: newScale,
        offset: { x: mid.x - cx * newScale, y: mid.y - cy * newScale },
      }));
    };

    const onUp = (e: PointerEvent) => {
      if (e.pointerType !== "touch" || !touches.has(e.pointerId)) return;
      touches.delete(e.pointerId);
      if (!gesture) return;
      e.stopPropagation();
      if (touches.size === 0) gesture = null;
    };

    container.addEventListener("pointerdown", onDown, true);
    container.addEventListener("pointermove", onMove, true);
    container.addEventListener("pointerup", onUp, true);
    container.addEventListener("pointercancel", onUp, true);
    return () => {
      container.removeEventListener("pointerdown", onDown, true);
      container.removeEventListener("pointermove", onMove, true);
      container.removeEventListener("pointerup", onUp, true);
      container.removeEventListener("pointercancel", onUp, true);
    };
  }, []);

  // Select zone (for editing, not zoom)
  const selectZone = useCallback((zoneId: string) => {
    setSelectedZoneId(zoneId);
    setSelectedSlotId(null);
  }, []);

  // Select slot
  const selectSlot = useCallback((slotId: string) => {
    setSelectedSlotId(slotId);
    setSelectedZoneId(null);
  }, []);

  return {
    warehouse,
    viewState,
    currentZone,
    selectedSlot,
    selectedSlotId,
    selectedZoneId,
    selectedZone,
    tool,
    setTool,
    isDrawing,
    currentRect,
    isPanning,
    isResizing,
    isDragging,
    dragSlotPreview,
    placingSlot,
    placingCursor,
    canvasContainerRef,
    setFloorPlan,
    clearFloorPlan,
    addZone,
    addSlotToZone,
    deleteZone,
    deleteSlot,
    zoomToZone,
    zoomOutToWarehouse,
    zoomIn,
    zoomOut,
    selectZone,
    selectSlot,
    setSelectedSlotId,
    clearSelection,
    renameZone,
    renameSlot,
    updateSlotItem,
    addSlotItem,
    deleteSlotItem,
    startResize,
    startDrag,
    startPan,
    startPlacingSlot,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handlePanStart,
    handlePanMove,
    handlePanEnd,
  };
}
